import { MESSAGE_TYPE } from 'shared/constants/messages';
import { applyPageFilters, applyRoleFilter, sortComparator } from './helpers';
import filterQueryGenerator from 'dashboard/helper/filterQueryGenerator';
import { matchesFilters } from './helpers/filterHelpers';
import {
  getUserPermissions,
  getUserRole,
} from '../../../helper/permissionsHelper';
import camelcaseKeys from 'camelcase-keys';

// The "Mine" view keeps a conversation visible after it is moved to stand_by
const MINE_STATUSES = ['open', 'stand_by'];

export const getSelectedChatConversation = ({
  allConversations,
  selectedChatId,
}) =>
  allConversations.filter(conversation => conversation.id === selectedChatId);

const getAssigneeId = conversation =>
  conversation?.assignee_id ?? conversation?.meta?.assignee?.id;

const isAssignedToCurrentUser = (conversation, currentUserId) => {
  const assigneeId = getAssigneeId(conversation);
  if (!assigneeId) return true;
  if (!currentUserId) return false;
  return String(assigneeId) === String(currentUserId);
};

const isOperatorUnreadEligible = (conversation, currentUserId) => {
  if (!conversation) return false;
  if (conversation.status !== 'open') return false;
  return isAssignedToCurrentUser(conversation, currentUserId);
};

const isStandByUnreadEligible = (conversation, currentUserId) => {
  if (!conversation) return false;
  if (conversation.status !== 'stand_by') return false;
  return isAssignedToCurrentUser(conversation, currentUserId);
};

const passesDialogueSegregation = (conversation, rootGetters) => {
  const currentUser = rootGetters.getCurrentUser;
  if (currentUser?.role === 'administrator') return true;

  const myTeamIds = (rootGetters['teams/getMyTeams'] || []).map(t => t.id);
  const teamId = conversation.team_id || conversation.meta?.team?.id || null;

  if (myTeamIds.length) return myTeamIds.includes(teamId);
  return teamId === null;
};

const findConversation = (state, conversationId) => {
  const normalizedId = Number(conversationId);
  return (
    state.allConversations.find(conv => conv.id === normalizedId) ||
    state.sidebarCountsData.find(conv => conv.id === normalizedId)
  );
};

const getters = {
  getAllConversations: _state => {
    const { allConversations, chatSortFilter: sortKey } = _state;
    return allConversations
      .slice()
      .sort((a, b) => sortComparator(a, b, sortKey));
  },
  getFilteredConversations: (
    { allConversations, chatSortFilter, appliedFilters },
    _,
    __,
    rootGetters
  ) => {
    const currentUser = rootGetters.getCurrentUser;
    const currentUserId = rootGetters.getCurrentUser.id;
    const currentAccountId = rootGetters.getCurrentAccountId;

    const permissions = getUserPermissions(currentUser, currentAccountId);
    const userRole = getUserRole(currentUser, currentAccountId);

    return allConversations
      .filter(conversation => {
        const allowedForRole = applyRoleFilter(
          conversation,
          userRole,
          permissions,
          currentUserId
        );
        if (!allowedForRole) return false;

        return matchesFilters(conversation, appliedFilters);
      })
      .sort((a, b) => sortComparator(a, b, chatSortFilter));
  },
  getSelectedChat: ({ selectedChatId, allConversations }) => {
    const selectedChat = allConversations.find(
      conversation => conversation.id === selectedChatId
    );
    return selectedChat || {};
  },
  getSelectedChatAttachments: ({ selectedChatId, attachments }) => {
    return attachments[selectedChatId] || [];
  },
  getChatListFilters: ({ conversationFilters }) => conversationFilters,
  getLastEmailInSelectedChat: (stage, _getters) => {
    const selectedChat = _getters.getSelectedChat;
    const { messages = [] } = selectedChat;
    const lastEmail = [...messages].reverse().find(message => {
      const { message_type: messageType } = message;
      if (message.private) return false;

      return [MESSAGE_TYPE.OUTGOING, MESSAGE_TYPE.INCOMING].includes(
        messageType
      );
    });

    return lastEmail;
  },
  getMineChats: (_state, _, __, rootGetters) => activeFilters => {
    const currentUserID = rootGetters.getCurrentUser?.id;
    const filters =
      activeFilters.status === 'open'
        ? { ...activeFilters, status: MINE_STATUSES }
        : activeFilters;

    return _state.allConversations
      .filter(conversation => {
        const { assignee } = conversation.meta;
        const isAssignedToMe = assignee && assignee.id === currentUserID;
        const shouldFilter = applyPageFilters(conversation, filters);
        return isAssignedToMe && shouldFilter;
      })
      .sort((a, b) => sortComparator(a, b, _state.chatSortFilter));
  },
  getAppliedConversationFiltersV2: _state => {
    // TODO: Replace existing one with V2 after migrating the filters to use camelcase
    return _state.appliedFilters.map(camelcaseKeys);
  },
  getAppliedConversationFilters: _state => {
    return _state.appliedFilters;
  },
  getAppliedConversationFiltersQuery: _state => {
    const hasAppliedFilters = _state.appliedFilters.length !== 0;
    return hasAppliedFilters ? filterQueryGenerator(_state.appliedFilters) : [];
  },
  getUnAssignedChats: _state => activeFilters => {
    return _state.allConversations
      .filter(conversation => {
        const { assignee } = conversation.meta;
        const isUnAssigned = !assignee || !assignee.id;
        const shouldFilter = applyPageFilters(conversation, activeFilters);
        return isUnAssigned && shouldFilter;
      })
      .sort((a, b) => sortComparator(a, b, _state.chatSortFilter));
  },
  getResolvedChats: _state => activeFilters => {
    return _state.allConversations
      .filter(conversation => {
        const isResolved = conversation.status === 'resolved';
        const shouldFilter = applyPageFilters(conversation, activeFilters);
        return isResolved && shouldFilter;
      })
      .sort((a, b) => sortComparator(a, b, 'last_activity_at_desc'));
  },
  getAllStatusChats: (_state, _, __, rootGetters) => activeFilters => {
    const currentUser = rootGetters.getCurrentUser;
    const currentUserId = rootGetters.getCurrentUser.id;
    const currentAccountId = rootGetters.getCurrentAccountId;

    const permissions = getUserPermissions(currentUser, currentAccountId);
    const userRole = getUserRole(currentUser, currentAccountId);

    return _state.allConversations
      .filter(conversation => {
        const shouldFilter = applyPageFilters(conversation, activeFilters);
        const allowedForRole = applyRoleFilter(
          conversation,
          userRole,
          permissions,
          currentUserId
        );

        return shouldFilter && allowedForRole;
      })
      .sort((a, b) => sortComparator(a, b, _state.chatSortFilter));
  },
  getChatListLoadingStatus: ({ listLoadingStatus }) => listLoadingStatus,
  getAllMessagesLoaded(_state) {
    const [chat] = getSelectedChatConversation(_state);
    return !chat || chat.allMessagesLoaded === undefined
      ? false
      : chat.allMessagesLoaded;
  },
  getUnreadCount(_state) {
    const [chat] = getSelectedChatConversation(_state);
    if (!chat) return [];
    return chat.messages.filter(
      chatMessage =>
        chatMessage.created_at * 1000 > chat.agent_last_seen_at * 1000 &&
        chatMessage.message_type === 0 &&
        chatMessage.private !== true
    ).length;
  },
  getChatStatusFilter: ({ chatStatusFilter }) => chatStatusFilter,
  getChatSortFilter: ({ chatSortFilter }) => chatSortFilter,
  getSelectedInbox: ({ currentInbox }) => currentInbox,
  getConversationById: _state => conversationId => {
    return _state.allConversations.find(
      value => value.id === Number(conversationId)
    );
  },
  getConversationParticipants: _state => {
    return _state.conversationParticipants;
  },
  getConversationLastSeen: _state => {
    return _state.conversationLastSeen;
  },

  getContextMenuChatId: _state => {
    return _state.contextMenuChatId;
  },

  getCopilotAssistant: _state => {
    return _state.copilotAssistant;
  },

  getTotalUnreadCount: _state => {
    return _state.cachedUnreadCounts?.total || 0;
  },

  getUnreadCountForInbox: _state => inboxId => {
    return _state.cachedUnreadCounts?.byInbox?.[inboxId] || 0;
  },

  getUnreadCountForLabel: _state => labelTitle => {
    return _state.cachedUnreadCounts?.byLabel?.[labelTitle] || 0;
  },

  getUnreadCountForTeam: _state => teamId => {
    return _state.cachedUnreadCounts?.byTeam?.[teamId] || 0;
  },

  // Get conversations for tab counts in label views
  getConversationsForLabelTabs: _state => label => {
    const source =
      _state.sidebarCountsData.length > 0
        ? _state.sidebarCountsData
        : _state.allConversations;

    return source.filter(conversation => {
      if (!conversation.labels || !Array.isArray(conversation.labels)) {
        return false;
      }
      return conversation.labels.includes(label);
    });
  },

  // Get conversations for tab counts in team views
  getConversationsForTeamTabs: _state => teamId => {
    const source =
      _state.sidebarCountsData.length > 0
        ? _state.sidebarCountsData
        : _state.allConversations;

    return source.filter(conversation => {
      const convTeamId = conversation.team_id || conversation.meta?.team?.id;
      return convTeamId === Number(teamId);
    });
  },

  getConversationsForInboxTabs: _state => inboxId => {
    const source =
      _state.sidebarCountsData.length > 0
        ? _state.sidebarCountsData
        : _state.allConversations;

    if (!inboxId) {
      return source;
    }

    return source.filter(conversation => {
      return conversation.inbox_id === Number(inboxId);
    });
  },

  // Operator Notifications (chatwoot-extra integration)
  getOperatorUnreadCount:
    (_state, _getters, _rootState, rootGetters) => conversationId => {
      const currentUserId = rootGetters.getCurrentUser?.id;
      const conversation = findConversation(_state, conversationId);
      if (
        !isOperatorUnreadEligible(conversation, currentUserId) &&
        !isStandByUnreadEligible(conversation, currentUserId)
      )
        return 0;
      if (!passesDialogueSegregation(conversation, rootGetters)) return 0;
      return _state.operatorNotifications.get(String(conversationId)) || 0;
    },

  getTotalOperatorUnreadCount: (_state, _getters, _rootState, rootGetters) => {
    const source =
      _state.sidebarCountsData.length > 0
        ? _state.sidebarCountsData
        : _state.allConversations;
    const currentUserId = rootGetters.getCurrentUser?.id;

    if (source.length === 0) return 0;

    return source.reduce((total, conv) => {
      if (!isOperatorUnreadEligible(conv, currentUserId)) return total;
      if (!passesDialogueSegregation(conv, rootGetters)) return total;
      return total + (_state.operatorNotifications.get(String(conv.id)) || 0);
    }, 0);
  },

  getOperatorUnreadCountForInbox:
    (_state, selfGetters, _rootState, rootGetters) => inboxId => {
      const source =
        _state.sidebarCountsData.length > 0
          ? _state.sidebarCountsData
          : _state.allConversations;
      const currentUserId = rootGetters.getCurrentUser?.id;

      return source
        .filter(conv => {
          if (conv.inbox_id !== inboxId) return false;
          const assigneeId = conv.assignee_id ?? conv.meta?.assignee?.id;
          if (assigneeId && String(assigneeId) === String(currentUserId))
            return false;
          if (!isOperatorUnreadEligible(conv, currentUserId)) return false;
          return passesDialogueSegregation(conv, rootGetters);
        })
        .reduce((total, conv) => {
          return total + selfGetters.getOperatorUnreadCount(conv.id);
        }, 0);
    },

  getOperatorUnreadCountForTeam:
    (_state, selfGetters, _rootState, rootGetters) => teamId => {
      const source =
        _state.sidebarCountsData.length > 0
          ? _state.sidebarCountsData
          : _state.allConversations;
      const currentUserId = rootGetters.getCurrentUser?.id;

      return source
        .filter(conv => {
          const convTeamId = conv.team_id || conv.meta?.team?.id;
          if (convTeamId !== teamId) return false;
          const assigneeId = conv.assignee_id ?? conv.meta?.assignee?.id;
          if (assigneeId && String(assigneeId) === String(currentUserId))
            return false;
          if (!isOperatorUnreadEligible(conv, currentUserId)) return false;
          return passesDialogueSegregation(conv, rootGetters);
        })
        .reduce((total, conv) => {
          return total + selfGetters.getOperatorUnreadCount(conv.id);
        }, 0);
    },

  getOperatorUnreadCountForLabel:
    (_state, selfGetters, _rootState, rootGetters) => labelTitle => {
      const source =
        _state.sidebarCountsData.length > 0
          ? _state.sidebarCountsData
          : _state.allConversations;
      const currentUserId = rootGetters.getCurrentUser?.id;

      return source
        .filter(conv => {
          if (!conv.labels || !Array.isArray(conv.labels)) return false;
          const assigneeId = conv.assignee_id ?? conv.meta?.assignee?.id;
          if (assigneeId && String(assigneeId) === String(currentUserId))
            return false;
          if (!isOperatorUnreadEligible(conv, currentUserId)) return false;
          if (!passesDialogueSegregation(conv, rootGetters)) return false;

          return conv.labels.some(label =>
            typeof label === 'string'
              ? label === labelTitle
              : label.title === labelTitle
          );
        })
        .reduce((total, conv) => {
          return total + selfGetters.getOperatorUnreadCount(conv.id);
        }, 0);
    },

  getSidebarAllCount: (_state, _getters, _rootState, rootGetters) => {
    const source =
      _state.sidebarCountsData.length > 0
        ? _state.sidebarCountsData
        : _state.allConversations;

    return source.filter(conv => {
      if (!passesDialogueSegregation(conv, rootGetters)) return false;
      const assigneeId = conv.assignee_id ?? conv.meta?.assignee?.id;
      const isUnassigned = conv.status === 'open' && !assigneeId;
      const isPending = conv.status === 'pending';
      return isUnassigned || isPending;
    }).length;
  },

  getSidebarUnattendedCount: (_state, _getters, _rootState, rootGetters) => {
    const source =
      _state.sidebarCountsData.length > 0
        ? _state.sidebarCountsData
        : _state.allConversations;
    const currentUserId = rootGetters.getCurrentUser?.id;

    return source.filter(conv => {
      if (conv.status !== 'open' && conv.status !== 'pending') return false;
      if (!passesDialogueSegregation(conv, rootGetters)) return false;
      const assigneeId = conv.assignee_id ?? conv.meta?.assignee?.id;
      if (assigneeId && String(assigneeId) === String(currentUserId))
        return false;
      return !conv.first_reply_created_at || conv.waiting_since;
    }).length;
  },

  getSidebarMineCount: (_state, _getters, _rootState, rootGetters) => {
    const source =
      _state.sidebarCountsData.length > 0
        ? _state.sidebarCountsData
        : _state.allConversations;
    const currentUserId = rootGetters.getCurrentUser?.id;

    return source.filter(conv => {
      if (!passesDialogueSegregation(conv, rootGetters)) return false;
      const assigneeId = conv.assignee_id ?? conv.meta?.assignee?.id;
      return (
        assigneeId === currentUserId && MINE_STATUSES.includes(conv.status)
      );
    }).length;
  },

  getSidebarStandByCount: (_state, _getters, _rootState, rootGetters) => {
    const source =
      _state.sidebarCountsData.length > 0
        ? _state.sidebarCountsData
        : _state.allConversations;

    return source.filter(conv => {
      if (!passesDialogueSegregation(conv, rootGetters)) return false;
      return conv.status === 'stand_by';
    }).length;
  },

  getSidebarTotalCountForTeam: _state => teamId => {
    const source =
      _state.sidebarCountsData.length > 0
        ? _state.sidebarCountsData
        : _state.allConversations;

    return source.filter(conv => {
      const convTeamId = conv.team_id || conv.meta?.team?.id;
      if (convTeamId !== Number(teamId)) return false;
      const assigneeId = conv.assignee_id ?? conv.meta?.assignee?.id;
      const isUnassigned = conv.status === 'open' && !assigneeId;
      const isPending = conv.status === 'pending';
      return isUnassigned || isPending;
    }).length;
  },

  getConversationTopic: _state => conversationId => {
    return _state.conversationTopics.get(String(conversationId)) || null;
  },

  getUnattendedOperatorUnreadCount: (
    _state,
    _getters,
    _rootState,
    rootGetters
  ) => {
    const source =
      _state.sidebarCountsData.length > 0
        ? _state.sidebarCountsData
        : _state.allConversations;
    const currentUserId = rootGetters.getCurrentUser?.id;

    if (source.length === 0) return 0;

    return source.reduce((total, conv) => {
      if (conv.status !== 'open' && conv.status !== 'pending') return total;
      const isUnattended = !conv.first_reply_created_at || conv.waiting_since;
      if (!isUnattended) return total;
      const assigneeId = conv.assignee_id ?? conv.meta?.assignee?.id;
      if (assigneeId && String(assigneeId) === String(currentUserId))
        return total;
      if (!passesDialogueSegregation(conv, rootGetters)) return total;
      return total + (_state.operatorNotifications.get(String(conv.id)) || 0);
    }, 0);
  },

  getAllSectionOperatorUnreadCount: (
    _state,
    _getters,
    _rootState,
    rootGetters
  ) => {
    const source =
      _state.sidebarCountsData.length > 0
        ? _state.sidebarCountsData
        : _state.allConversations;
    const currentUserId = rootGetters.getCurrentUser?.id;

    if (source.length === 0) return 0;

    return source.reduce((total, conv) => {
      if (!passesDialogueSegregation(conv, rootGetters)) return total;
      const assigneeId = conv.assignee_id ?? conv.meta?.assignee?.id;
      const isUnassigned = conv.status === 'open' && !assigneeId;
      const isPending = conv.status === 'pending';
      if (!isUnassigned && !isPending) return total;
      if (!isOperatorUnreadEligible(conv, currentUserId)) return total;
      return total + (_state.operatorNotifications.get(String(conv.id)) || 0);
    }, 0);
  },

  getMineOperatorUnreadCount: (_state, _getters, _rootState, rootGetters) => {
    const source =
      _state.sidebarCountsData.length > 0
        ? _state.sidebarCountsData
        : _state.allConversations;
    const currentUserId = rootGetters.getCurrentUser?.id;

    if (source.length === 0) return 0;

    return source.reduce((total, conv) => {
      if (!passesDialogueSegregation(conv, rootGetters)) return total;
      const assigneeId = conv.assignee_id ?? conv.meta?.assignee?.id;
      if (assigneeId !== currentUserId || conv.status !== 'open') return total;
      return total + (_state.operatorNotifications.get(String(conv.id)) || 0);
    }, 0);
  },

  getStandByOperatorUnreadCount: (
    _state,
    _getters,
    _rootState,
    rootGetters
  ) => {
    const source =
      _state.sidebarCountsData.length > 0
        ? _state.sidebarCountsData
        : _state.allConversations;
    const currentUserId = rootGetters.getCurrentUser?.id;

    if (source.length === 0) return 0;

    return source.reduce((total, conv) => {
      if (!isStandByUnreadEligible(conv, currentUserId)) return total;
      if (!passesDialogueSegregation(conv, rootGetters)) return total;
      return total + (_state.operatorNotifications.get(String(conv.id)) || 0);
    }, 0);
  },
};

export default getters;
